import React, { FC, Fragment, useState } from 'react';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CreateRoundedIcon from '@material-ui/icons/CreateRounded';
import RemoveCircleRoundedIcon from '@material-ui/icons/RemoveCircleRounded';
import DeleteIcon from '@material-ui/icons/Delete';
import { UseMutationOptions } from 'react-query';

import { Alert } from 'components';
import {
  GetUserSearchesV2QueryVariables,
  UpdateUserManagementUserProfileInput,
  UpdateUserManagementUserProfileMutation,
  UpdateUserManagementUserProfileMutationVariables,
  useDeleteUserManagementUserProfileMutation,
  UserProfileSearchFragment,
  UserStatus,
  useSuspendUserManagementUserProfileMutation,
  useUnsuspendUserManagementUserProfileMutation,
  useUpdateUserManagementUserProfileMutation,
} from 'api';
import {
  useDeleteUserManagementUserProfile,
  useSuspendUserManagementUserProfile,
  useUnsuspendUserManagementUserProfile,
  useUpdateUserManagementUserProfile,
} from 'features/admin/updaters';
import { useNotify } from 'hooks';

import { EditDialog } from './index';

enum Action {
  Update,
  Remove,
  Suspend,
  Unsuspend,
  None,
}

export type UpdateUserManagementUserProfileOptions = UseMutationOptions<
  UpdateUserManagementUserProfileMutation,
  unknown,
  UpdateUserManagementUserProfileMutationVariables
>;

type Variables = UpdateUserManagementUserProfileMutationVariables;

interface Props {
  userProfile: UserProfileSearchFragment;
  count: number;
  queryVariables: GetUserSearchesV2QueryVariables;
  mutationVariables?: Variables;
}

const Actions: FC<Props> = ({ userProfile, queryVariables }) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const [action, setAction] = useState<Action>(Action.None);
  // TODO: user status needs update from BE
  const isSuspended = userProfile.user_status !== UserStatus.Active;

  const suspendUserManagementUserProfileOptions =
    useSuspendUserManagementUserProfile(queryVariables);
  const { mutate: suspendUserProfileMutation } = useSuspendUserManagementUserProfileMutation({
    ...suspendUserManagementUserProfileOptions,
    onSuccess: handleSuspendSuccess,
  });

  const unsuspendUserManagementUserProfileOptions =
    useUnsuspendUserManagementUserProfile(queryVariables);
  const { mutate: unsuspendUserProfileMutation } = useUnsuspendUserManagementUserProfileMutation({
    ...unsuspendUserManagementUserProfileOptions,
    onSuccess: handleUnsuspendSuccess,
  });

  const updateUserManagementUserProfileOptions = useUpdateUserManagementUserProfile(queryVariables);
  const { mutate: updateUserProfileMutation } = useUpdateUserManagementUserProfileMutation({
    ...updateUserManagementUserProfileOptions,
    onSuccess: handleUpdateSuccess,
  });

  const deleteUserManagementUserProfileOptions = useDeleteUserManagementUserProfile(queryVariables);
  const { mutate: deleteUserProfileMutation } = useDeleteUserManagementUserProfileMutation({
    ...deleteUserManagementUserProfileOptions,
    onSuccess: handleDeleteSuccess,
  });

  function handleSuspendSuccess() {
    notify.info({ message: t('admin:suspendUserMessage', { count: 1 }) });
  }

  function handleUnsuspendSuccess() {
    notify.info({ message: t('admin:unsuspendUserMessage', { count: 1 }) });
  }

  function handleUpdateSuccess() {
    notify.info({ message: t('admin:userUpdateSuccess') });
  }

  function handleDeleteSuccess() {
    notify.info({ message: t('admin:userDeleteSuccess', { count: 1 }) });
  }

  function handleCancel() {
    setAction(Action.None);
  }

  function handleProfileRemoval() {
    setAction(Action.None);
    deleteUserProfileMutation({ id: userProfile?.id });
  }

  function handleProfileStatusUpdate() {
    setAction(Action.None);

    switch (action) {
      case Action.Suspend:
        suspendUserProfileMutation({ id: userProfile?.id });
        break;
      case Action.Unsuspend:
        unsuspendUserProfileMutation({ id: userProfile?.id });
        break;
    }
  }

  function handleProfileUpdate(input?: UpdateUserManagementUserProfileInput) {
    setAction(Action.None);
    updateUserProfileMutation({ input });
  }

  function renderAlerts() {
    return (
      <Fragment>
        <Alert
          destructive
          open={action === Action.Remove}
          onConfirm={handleProfileRemoval}
          onCancel={handleCancel}
          message={t('admin:deleteUser', { fullName: userProfile?.full_name })}
          confirm={t('common:remove')}
        />
        <Alert
          destructive
          open={action === Action.Suspend}
          onConfirm={handleProfileStatusUpdate}
          onCancel={handleCancel}
          message={t('admin:suspendUser')}
          confirm={t('admin:suspend')}
        />
        {isSuspended && (
          <Alert
            destructive
            open={action === Action.Unsuspend}
            onConfirm={handleProfileStatusUpdate}
            onCancel={handleCancel}
            message={t('admin:unsuspendUser')}
            confirm={t('admin:unsuspend')}
          />
        )}
        <EditDialog
          userProfile={userProfile}
          open={action === Action.Update}
          onCancel={handleCancel}
          onConfirm={input => handleProfileUpdate(input)}
        />
      </Fragment>
    );
  }

  function renderRemovalIcon() {
    return (
      <Grid item>
        <Tooltip title={t('common:remove')}>
          <IconButton onClick={() => setAction(Action.Remove)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  function renderSuspendIcon() {
    if (isSuspended) {
      return (
        <Grid item>
          <Tooltip title={t('admin:unsuspend')}>
            <IconButton onClick={() => setAction(Action.Unsuspend)}>
              <RemoveCircleRoundedIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      );
    }

    return (
      <Grid item>
        <Tooltip title={t('admin:suspend')}>
          <IconButton onClick={() => setAction(Action.Suspend)}>
            <RemoveCircleRoundedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  function renderEditIcon() {
    return (
      <Grid item>
        <Tooltip title={t('common:edit')}>
          <IconButton onClick={() => setAction(Action.Update)}>
            <CreateRoundedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  return (
    <Fragment>
      {renderAlerts()}
      <Grid container alignItems="center" justify="center">
        {renderEditIcon()}
        {renderSuspendIcon()}
        {isSuspended && renderRemovalIcon()}
      </Grid>
    </Fragment>
  );
};

export default Actions;
